import { useAiTags } from '@solinkcorp/ai-tags';
import { type AiTagId } from '@solinkcorp/api-types';
import { type Immutable } from 'immer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useControllerState } from 'solox';
import { DEBUGGER_PANEL_MAX_WIDTH_PX, DEBUGGER_PANEL_MIN_WIDTH_PX } from '../../constants';
import type { Controller } from '../../Controller';
import { useResizePx } from '../../hooks/useResizePx';
import { startOfLocalDay, startOfLocalHour } from '../../layout/getHorizonWindow';
import {
  averageZoneTimeSecondsSince,
  countEventsSince,
  type ServiceDisplayEventsController,
} from '../../ServiceDisplayEventsController';
import { type EventPair, type SdmEvent, type ServiceDisplayEventId } from '../../types';
import './ServiceDisplayDebugger.css';
import { type DebugEventType } from './ServiceDisplayDebuggerClass';
import { TestingTab, type TestingLogEntry } from './testing/TestingTab';

interface DebugEvent {
  id: string;
  timestamp: number;
  type: DebugEventType;
  message: string;
  data?: any;
  formattedData?: string;
}

interface ServiceDisplayDebuggerProps {
  isEnabled: boolean;
  isMinimized: boolean;
  onToggle: () => void;
  eventsController: ServiceDisplayEventsController;
  sdmController: Controller;
  widthPx: number;
  onWidthChange: (width: number) => void;
}

type EventFilter =
  'ALL' | 'RT_ZONE' | 'onEvent' | 'prune' | 'line_count' | 'Errors' | 'Summary' | 'testing';
type AiTagFilter = 'ALL' | string;

export const ServiceDisplayDebugger: React.FC<ServiceDisplayDebuggerProps> = ({
  isEnabled,
  isMinimized,
  onToggle,
  eventsController,
  sdmController,
  widthPx,
  onWidthChange,
}) => {
  const [events, setEvents] = useState<DebugEvent[]>([]);
  const [maxEvents, setMaxEvents] = useState(50);
  const [activeFilter, setActiveFilter] = useState<EventFilter>('ALL');
  const [activeAiTagFilter, setActiveAiTagFilter] = useState<AiTagFilter>('ALL');
  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(null);
  const [aiTagSummaryMap, setAiTagSummaryMap] = useState<Map<string, any>>(new Map());
  // Sticky logs for the testing tab. Never trimmed — survive the rolling `maxEvents` cap.
  const [testingLogs, setTestingLogs] = useState<TestingLogEntry[]>([]);
  const eventsListRef = useRef<HTMLDivElement>(null);
  const debuggerRef = useRef<HTMLDivElement>(null);

  const { data: aiTags } = useAiTags();
  const tagNameById = useMemo(() => {
    const map = new Map<string, string>();
    if (aiTags) {
      for (const tag of aiTags) {
        if (tag.id) map.set(tag.id, tag.name);
      }
    }
    return map;
  }, [aiTags]);

  // Union of all tagIds referenced by widgets in the current layout (editing takes precedence
  // over the saved selection). Simulated events use this so every widget matches.
  const widgetTagIds = useControllerState(sdmController.state, (state) => {
    const widgets = state.editingLayout?.widgets ?? state.selectedLayout?.widgets ?? [];
    const ids = new Set<string>();
    for (const widget of widgets) {
      for (const id of widget.tagIds) ids.add(id);
    }
    return Array.from(ids);
  });

  const appendTestingLog = useCallback((entry: TestingLogEntry) => {
    setTestingLogs((prev) => [...prev, entry]);
  }, []);

  // Subscribe to rawEvents so the Summary tab's on-demand counts/averages recompute when
  // events arrive. Memoized helpers below treat this reference as a dep.
  const { rawEvents, windowRangeMs } = useControllerState(eventsController.state, (state) => ({
    rawEvents: state.rawEvents,
    windowRangeMs: state.windowRangeMs,
  }));

  const { resizeRef } = useResizePx({
    width: widthPx,
    onWidthChange,
    minWidth: DEBUGGER_PANEL_MIN_WIDTH_PX,
    maxWidth: DEBUGGER_PANEL_MAX_WIDTH_PX,
    direction: 'left',
  });

  // Add event to debugger
  const addEvent = useCallback(
    (event: Omit<DebugEvent, 'id' | 'timestamp'>) => {
      if (!isEnabled) return;

      const newEvent: DebugEvent = {
        ...event,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
      };

      setEvents((prev) => {
        const updated = [...prev, newEvent];
        return updated.slice(-maxEvents); // Keep only last N events
      });
    },
    [isEnabled, maxEvents],
  );

  const updateSummaryData = useCallback((summaryMap: Map<string, any>) => {
    setAiTagSummaryMap(new Map(summaryMap));
  }, []);

  // Register callbacks with the controller's debugger class
  useEffect(() => {
    if (isEnabled) {
      eventsController.registerDebuggerCallbacks(addEvent, updateSummaryData);
    } else {
      eventsController.unregisterDebuggerCallbacks();
    }
  }, [isEnabled, addEvent, updateSummaryData, eventsController]);

  const clearEvents = () => {
    setEvents([]);
  };

  const toggleEventHighlight = (eventId: string) => {
    setHighlightedEventId(highlightedEventId === eventId ? null : eventId);
  };

  // Get all unique AI tags from events and summary data
  const getUniqueAiTags = (): string[] => {
    const aiTags = new Set<string>();

    // Add AI tags from events
    events.forEach((event) => {
      // Check for aiTagId in event data
      if (event.data?.aiTagId) {
        aiTags.add(event.data.aiTagId);
      }
      // Check for aiTagIds in the raw event data (for onEvent)
      if (event.data?.metadata?.aiTagIds) {
        event.data.metadata.aiTagIds.forEach((tag: string) => aiTags.add(tag));
      }
      if (event.data?.event_line?.metadata?.aiTagIds) {
        event.data.event_line.metadata.aiTagIds.forEach((tag: string) => aiTags.add(tag));
      }
    });

    // Add AI tags from summary data
    aiTagSummaryMap.forEach((_, aiTagId) => {
      aiTags.add(aiTagId);
    });

    return Array.from(aiTags).sort();
  };

  const uniqueAiTags = getUniqueAiTags();

  // Helper to check if an event is an error type
  const isErrorType = (type: DebugEvent['type']): boolean => {
    return (
      type === 'validation_error' ||
      type === 'processing_error' ||
      type === 'connection_error' ||
      type === 'historical_data_error'
    );
  };

  // Filter events based on active filter and reverse order (newest first)
  const filteredEvents = events
    .filter((event) => {
      // Filter by event type
      let typeMatch: boolean;
      if (activeFilter === 'ALL') {
        typeMatch = true;
      } else if (activeFilter === 'Errors') {
        typeMatch = isErrorType(event.type);
      } else {
        typeMatch = event.type === activeFilter;
      }

      // Filter by AI tag
      const hasAiTag = (eventData: any, targetTag: string): boolean => {
        if (eventData?.aiTagId === targetTag) return true;
        if (eventData?.metadata?.aiTagIds?.includes(targetTag)) return true;
        if (eventData?.event_line?.metadata?.aiTagIds?.includes(targetTag)) return true;
        return false;
      };

      const aiTagMatch = activeAiTagFilter === 'ALL' || hasAiTag(event.data, activeAiTagFilter);

      return typeMatch && aiTagMatch;
    })
    .reverse(); // Reverse to show newest events first

  // Get event counts for each type
  const getEventCounts = () => {
    const errorCount = events.filter((e) => isErrorType(e.type)).length;
    const counts: Record<EventFilter, number> = {
      ALL: events.length,
      RT_ZONE: events.filter((e) => e.type === 'RT_ZONE').length,
      onEvent: events.filter((e) => e.type === 'onEvent').length,
      prune: events.filter((e) => e.type === 'prune').length,
      line_count: events.filter((e) => e.type === 'line_count').length,
      Errors: errorCount,
      Summary: aiTagSummaryMap.size,
      testing: testingLogs.length,
    };
    return counts;
  };

  const eventCounts = getEventCounts();

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getEventTypeColor = (type: DebugEvent['type']) => {
    switch (type) {
      case 'RT_ZONE':
        return '#4CAF50';
      case 'onEvent':
        return '#2196F3';
      case 'prune':
        return '#FF9800';
      case 'line_count':
        return '#9C27B0';
      case 'clear':
        return '#FF5722';
      case 'testing':
        return '#00BCD4';
      case 'validation_error':
      case 'processing_error':
      case 'connection_error':
      case 'historical_data_error':
        return '#F44336';
      default:
        return '#666';
    }
  };

  const formatEventData = (data: any): string => {
    if (!data) return '';

    try {
      if (typeof data === 'string') {
        return data;
      }

      // Format common event structures
      if (data.evt_type === 'zone') {
        return `Zone: ${data.label} | Camera: ${data.cam_id} | Entry: ${
          data.entry_ms ? new Date(data.entry_ms).toLocaleTimeString() : 'N/A'
        } | Exit: ${data.exit_ms ? new Date(data.exit_ms).toLocaleTimeString() : 'Active'}`;
      }

      if (data.evt_type === 'line') {
        return `Line: ${data.event_line?.label || 'Unknown'} | Camera: ${
          data.cam_id
        } | Time: ${new Date(data.start_ms).toLocaleTimeString()}`;
      }

      return JSON.stringify(data, null, 2);
    } catch (_error) {
      return String(data);
    }
  };

  const formatRTZoneEvent = (event: DebugEvent): string => {
    const { zoneLabel, entryMs, exitTime, uid } = event.data;
    const isEntering = exitTime === undefined || exitTime === null;
    const action = isEntering ? '🟢 Entering: ' : '🔴 Exiting: ';
    const received = new Date(event.timestamp).toLocaleTimeString();

    let timingInfo: string;
    let latencyInfo: string;
    if (isEntering) {
      timingInfo = `\nentry: ${new Date(entryMs).toLocaleTimeString()}\n `;
      latencyInfo = ` | lag ~${event.timestamp - entryMs}ms `;
    } else {
      timingInfo = `\nexit: ${new Date(exitTime).toLocaleTimeString()}\nduration: ${(
        (exitTime - entryMs) /
        1000
      ).toFixed(2)}s`;
      latencyInfo = ` | lag ~${event.timestamp - exitTime}ms `;
    }

    const debugMsg = `ID: ${uid} | 📬 ${received} ${latencyInfo}\n\n${action}${zoneLabel}\n${timingInfo}`;

    return debugMsg;
  };

  const formatLineCountEvent = (event: DebugEvent): string => {
    if (event.type !== 'line_count' || !event.data) {
      return event.message;
    }

    const { uid, enterMs, isHistorical } = event.data;
    const received = new Date(event.timestamp).toLocaleTimeString();

    return `Event ID: ${uid} | 📬 ${received}
${event.message}
Event time: ${new Date(enterMs).toLocaleTimeString()}
${
  isHistorical ? `Residency time: ${((event.timestamp - enterMs) / 60000).toPrecision(2)}mins` : ''
}`;
  };

  const excerptEventsSince = (
    rawEvents: ReadonlyMap<ServiceDisplayEventId, Immutable<EventPair>>,
    tagIds: AiTagId[],
    sinceMs: number,
    eventType: SdmEvent['evt_type'],
  ): string => {
    const tagSet = new Set(tagIds);
    const getEventStart = (event: Immutable<EventPair> | null): number => {
      if (!event) return Date.now();
      if (event.start?.evt_type === 'zone') return event.start.entry_ms;
      if (event.start?.evt_type === 'line') return event.start.start_ms;
      return Date.now();
    };
    const getEventEnd = (event: Immutable<EventPair> | null): number => {
      if (!event?.end) return Date.now();
      if (event.end.evt_type === 'zone') return event.end.exit_ms ?? Date.now();
      return event.end.end_ms;
    };
    const eventPairs = Array.from(rawEvents.values()).filter(
      (event) =>
        event.start?.evt_type === eventType &&
        event.start?.aiTagIds.some((tid: string) => tagSet.has(tid as AiTagId)) &&
        getEventEnd(event) >= sinceMs,
    );
    const pprint = (event: Immutable<EventPair>) =>
      `${((getEventEnd(event) - getEventStart(event)) / 1000).toFixed(2)}s`;
    return [
      ...eventPairs.slice(0, 3).map((event) => pprint(event)),
      ...(eventPairs.length > 6 ? [`[... ${eventPairs.length - 6} more]`] : ['']),
      ...eventPairs
        .slice(3)
        .slice(-3)
        .map((event) => pprint(event)),
    ].join(', ');
  };

  const formatSummaryData = (aiTagId: string, summary: any): string => {
    const displayName = summary.label || aiTagId;
    const activeEventsList =
      summary.activeZoneEvents.length > 0
        ? summary.activeZoneEvents
            .map((eventKey: string, index: number) => {
              // Parse the event key: "timestamp:aiTagId:camId:label:polygonId"
              const parts = eventKey.split(':');
              const timestamp = parts[0];
              const camId = parts[2];
              const label = parts[3];
              const polygonId = parts[4];
              const entryDate = new Date(Number(timestamp));
              const entryTime = `${entryDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${entryDate.toLocaleTimeString()}`;
              const startEvent = rawEvents.get(eventKey as ServiceDisplayEventId)?.start;
              const objectId = startEvent?.evt_type === 'zone' ? startEvent.object_id : undefined;
              return `  ${index + 1}. ${label} (cam: ${camId}, object_id: ${objectId ?? '?'}) - Entered: ${entryTime} [${polygonId}]`;
            })
            .join('\n')
        : '  None';

    // Derive historical aggregates on demand from `rawEvents`. The summary map no longer
    // stores precomputed totals because the retention window may extend past 1h to support
    // longer horizons; we explicitly bucket by the rolling 60 minutes here.
    const tagIds = [aiTagId] as AiTagId[];
    const oneHourAgoMs = Date.now() - 60 * 60 * 1000;
    const hourStartMs = startOfLocalHour(new Date());
    const dayStartMs = startOfLocalDay(new Date());
    const zoneTimeExcerpt = {
      allTime: excerptEventsSince(rawEvents, tagIds, 0, 'zone'),
      oneHourAgo: excerptEventsSince(rawEvents, tagIds, oneHourAgoMs, 'zone'),
      hourStart: excerptEventsSince(rawEvents, tagIds, hourStartMs, 'zone'),
      dayStart: excerptEventsSince(rawEvents, tagIds, dayStartMs, 'zone'),
    };

    return `AI Tag ID: ${aiTagId}
Label: ${displayName}

Vehicles in Zone Now: ${summary.totalVehiclesInZoneNow}
Active Zone Events (${summary.activeZoneEvents.length}):
${activeEventsList}

All-time aggregates (from T=${new Date(Date.now() - windowRangeMs).toLocaleString()}):
  Zone Count:       ${countEventsSince(rawEvents, tagIds, 0, 'zone')}
  Line Count:       ${countEventsSince(rawEvents, tagIds, 0, 'line')}
  Drive-off Count:  ${countEventsSince(rawEvents, tagIds, 0, 'driveOff')}
  Avg Zone Time:    ${averageZoneTimeSecondsSince(rawEvents, tagIds, 0).toFixed(2)}s (excerpt: ${zoneTimeExcerpt.allTime})
  
Past 60min aggregates (from T=${new Date(oneHourAgoMs).toLocaleTimeString()}):
  Zone Count:       ${countEventsSince(rawEvents, tagIds, oneHourAgoMs, 'zone')}
  Line Count:       ${countEventsSince(rawEvents, tagIds, oneHourAgoMs, 'line')}
  Drive-off Count:  ${countEventsSince(rawEvents, tagIds, oneHourAgoMs, 'driveOff')}
  Avg Zone Time:    ${averageZoneTimeSecondsSince(rawEvents, tagIds, oneHourAgoMs).toFixed(2)}s (excerpt: ${zoneTimeExcerpt.oneHourAgo})
  
Current hour aggregates (from T=${new Date(hourStartMs).toLocaleTimeString()}):
  Zone Count:       ${countEventsSince(rawEvents, tagIds, hourStartMs, 'zone')}
  Line Count:       ${countEventsSince(rawEvents, tagIds, hourStartMs, 'line')}
  Drive-off Count:  ${countEventsSince(rawEvents, tagIds, hourStartMs, 'driveOff')}
  Avg Zone Time:    ${averageZoneTimeSecondsSince(rawEvents, tagIds, hourStartMs).toFixed(2)}s (excerpt: ${zoneTimeExcerpt.hourStart})
  
Current day aggregates (from T=${new Date(dayStartMs).toLocaleTimeString()}):
  Zone Count:       ${countEventsSince(rawEvents, tagIds, dayStartMs, 'zone')}
  Line Count:       ${countEventsSince(rawEvents, tagIds, dayStartMs, 'line')}
  Drive-off Count:  ${countEventsSince(rawEvents, tagIds, dayStartMs, 'driveOff')}
  Avg Zone Time:    ${averageZoneTimeSecondsSince(rawEvents, tagIds, dayStartMs).toFixed(2)}s (excerpt: ${zoneTimeExcerpt.dayStart})
`;
  };

  if (!isEnabled) return null;

  return (
    <div
      ref={debuggerRef}
      className={`service-display-debugger ${isMinimized ? 'minimized' : ''} z-50`}
      style={{ width: `${widthPx}px` }}
    >
      {/* Resize handle */}
      <div className="resize-handle" ref={resizeRef} />
      <div className="debugger-header">
        <div className="debugger-title">
          D🐞BUGGER
          <span className="event-count">({eventCounts[activeFilter]})</span>
        </div>
        <div className="debugger-controls">
          <button
            className="debugger-btn"
            onClick={() => setMaxEvents((prev) => (prev === 50 ? 100 : 50))}
            title="Toggle max events"
          >
            Max: {maxEvents}
          </button>
          <button className="debugger-btn" onClick={clearEvents} title="Clear events">
            Clear
          </button>
          <button className="debugger-btn close-btn" onClick={onToggle} title="Close debugger">
            ✕
          </button>
        </div>
      </div>

      <div className="debugger-content">
        {/* Event Filter Tabs */}
        <div className="debugger-tabs">
          {(
            [
              'ALL',
              'RT_ZONE',
              'onEvent',
              'prune',
              'line_count',
              'Errors',
              'Summary',
              'testing',
            ] as EventFilter[]
          ).map((filter) => (
            <button
              key={filter}
              className={`debugger-tab ${activeFilter === filter ? 'active' : ''} ${
                filter === 'Errors' && eventCounts.Errors > 0 ? 'error-tab' : ''
              }`}
              onClick={() => setActiveFilter(filter)}
              title={`Show ${filter} events`}
            >
              {filter}
              <span className="tab-count">({eventCounts[filter]})</span>
            </button>
          ))}
        </div>

        {/* AI Tag Filter Tabs — hidden on the testing tab where they have no effect */}
        {activeFilter !== 'testing' && uniqueAiTags.length > 0 && (
          <div className="debugger-tabs ai-tag-tabs">
            <button
              className={`debugger-tab ${activeAiTagFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => setActiveAiTagFilter('ALL')}
              title="Show all AI tags"
            >
              AI TAGS
              <span className="tab-count">({events.length})</span>
            </button>
            {uniqueAiTags.map((aiTag) => {
              const hasAiTag = (eventData: any, targetTag: string): boolean => {
                if (eventData?.aiTagId === targetTag) return true;
                if (eventData?.metadata?.aiTagIds?.includes(targetTag)) return true;
                if (eventData?.event_line?.metadata?.aiTagIds?.includes(targetTag)) return true;
                return false;
              };
              const count = events.filter((e) => hasAiTag(e.data, aiTag)).length;
              const summary = aiTagSummaryMap.get(aiTag);
              const displayName = summary?.label || aiTag;
              return (
                <button
                  key={aiTag}
                  className={`debugger-tab ${activeAiTagFilter === aiTag ? 'active' : ''}`}
                  onClick={() => setActiveAiTagFilter(aiTag)}
                  title={`Show events for AI tag: ${displayName} (${aiTag})`}
                >
                  {displayName}
                  <span className="tab-count">({count})</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="events-list" ref={eventsListRef}>
          {activeFilter === 'testing' ? (
            <TestingTab
              eventsController={eventsController}
              widgetTagIds={widgetTagIds}
              tagNameById={tagNameById}
              testingLogs={testingLogs}
              onAppendLog={appendTestingLog}
            />
          ) : activeFilter === 'Summary' ? (
            // Show summary data
            aiTagSummaryMap.size === 0 ? (
              <div className="no-events">No AI tag summaries available...</div>
            ) : (
              Array.from(aiTagSummaryMap.entries())
                .filter(([aiTagId]) => activeAiTagFilter === 'ALL' || aiTagId === activeAiTagFilter)
                .map(([aiTagId, summary]) => (
                  <div key={aiTagId} className="debug-event">
                    <div className="event-header">
                      <span className="sdm-event-type" style={{ backgroundColor: '#2196f3' }}>
                        Summary
                      </span>
                      <span className="event-time">{formatTimestamp(Date.now())}</span>
                    </div>
                    <div className="event-message rt-zone-message">
                      {formatSummaryData(aiTagId, summary)}
                    </div>
                  </div>
                ))
            )
          ) : filteredEvents.length === 0 ? (
            <div className="no-events">
              {events.length === 0
                ? 'No events captured yet...'
                : `No ${activeFilter} events found`}
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className={`debug-event ${highlightedEventId === event.id ? 'highlighted' : ''}`}
                onClick={() => toggleEventHighlight(event.id)}
                title="Click to focus on event"
              >
                <div className="event-header">
                  <span
                    className="sdm-event-type"
                    style={{ backgroundColor: getEventTypeColor(event.type) }}
                  >
                    {event.type}
                  </span>
                  <span className="event-time">{formatTimestamp(event.timestamp)}</span>
                </div>
                <div
                  className={`event-message ${
                    event.type === 'RT_ZONE' || event.type === 'line_count' ? 'rt-zone-message' : ''
                  }`}
                >
                  {event.type === 'RT_ZONE'
                    ? formatRTZoneEvent(event)
                    : event.type === 'line_count'
                      ? formatLineCountEvent(event)
                      : event.message}
                </div>
                {event.data && event.type !== 'RT_ZONE' && event.type !== 'line_count' && (
                  <div className="event-data">
                    <pre>{event.formattedData || formatEventData(event.data)}</pre>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDisplayDebugger;
